import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import get from 'lodash/get';
import {
  Collapse,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  NavItem,
  UncontrolledDropdown,
} from 'reactstrap';
import { signOut } from 'actions/auth';
import ContactModal from 'components/ContactModal';
import { ButtonLink } from 'components/Button/ButtonLink';
import { numberUnmask, formatPhone } from 'utils/masks';
import { useConfig } from 'config/ConfigContext';
import LusaSimpleLogo from 'assets/icons/lusa-simple-logo.svg';
import './style.scss';

export const HeaderComponent = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loanDetail } = props;
  const { envLabel, collectionsPhone, servicingPhone } = useConfig();
  const [dropdown, setDropdown] = useState(false);
  const [modal, setModal] = useState(false);
  const pastDue = useMemo(() => Number(get(loanDetail, 'data.0.daysPastDue', 0)), [loanDetail]);
  const phoneNo = useMemo(() => (pastDue > 16 ? collectionsPhone : servicingPhone), [pastDue, collectionsPhone, servicingPhone]);

  const toggle = () => {
    setDropdown(!dropdown);
  };

  const handlePageNavigation = (route, e) => {
    e.preventDefault();
    navigate(route);
  };

  const handleSignOut = () => {
    props.signOut(navigate);
  };

  const handleModal = () => {
    setModal(!modal);
  };

  if (location.pathname.indexOf('quickpay') !== -1) {
    return (
      <div className="d-flex align-items-center position-relative mobile-header">
        <img src={LusaSimpleLogo} alt="Servicing USA" />
        { envLabel && envLabel !== 'production' && <div className="envLabel">{ envLabel }</div> }
      </div>
    );
  }

  // While the user is on a two-factor setup/enroll screen, or is signed in but
  // has not yet satisfied MFA, they must not be able to navigate away — the only
  // action available is Sign Out. Detect that state so we can render the primary
  // nav links (and Account Settings) as non-clickable.
  const onTwoFactorFlow = location.pathname.indexOf('/two-factor') !== -1;
  const mfaNotSatisfied = sessionStorage.getItem('idToken')
    && sessionStorage.getItem('mfaSatisfied') !== 'true';
  const navLocked = onTwoFactorFlow || mfaNotSatisfied;

  const handleLockedNavClick = (e) => {
    e.preventDefault();
  };

  return (
    <div>
      <Navbar light expand="lg" className="consumer-header">
        <div className="d-flex align-items-center position-relative">
          <NavbarBrand className="text-hide" onClick={handlePageNavigation.bind(null, '/consumer/dashboard')}>LendingUSA Portal</NavbarBrand>
          { envLabel && envLabel !== 'production' && <div className="envLabel">{ envLabel }</div> }
        </div>
        {
          sessionStorage.idToken
            ? (
              <>
                <NavbarToggler onClick={toggle} />
                <Collapse isOpen={dropdown} navbar>
                  <Nav navbar className="w-100">
                    <div className="lu-main-nav d-lg-flex ml-lg-auto">
                      <NavItem>
                        <NavLink
                          to="/consumer/dashboard"
                          className={`nav-link${navLocked ? ' nav-link--disabled' : ''}`}
                          onClick={navLocked ? handleLockedNavClick : undefined}
                          tabIndex={navLocked ? -1 : undefined}
                          aria-disabled={navLocked || undefined}
                        >
                          Dashboard
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          to="/consumer/loans"
                          className={`nav-link${navLocked ? ' nav-link--disabled' : ''}`}
                          onClick={navLocked ? handleLockedNavClick : undefined}
                          tabIndex={navLocked ? -1 : undefined}
                          aria-disabled={navLocked || undefined}
                        >
                          My Loan
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          to="/consumer/documents"
                          className={`nav-link${navLocked ? ' nav-link--disabled' : ''}`}
                          onClick={navLocked ? handleLockedNavClick : undefined}
                          tabIndex={navLocked ? -1 : undefined}
                          aria-disabled={navLocked || undefined}
                        >
                          Documents
                        </NavLink>
                      </NavItem>
                      <NavItem className="--hide-above-small">
                        <ButtonLink
                          onClick={navLocked ? undefined : handleModal}
                          isDisabled={navLocked}
                          className={`nav-link${navLocked ? ' nav-link--disabled' : ''}`}
                          style={{ cursor: navLocked ? 'not-allowed' : 'pointer' }}
                        >
                          Contact Us
                        </ButtonLink>
                      </NavItem>
                    </div>
                    <div className="lu-second-nav d-lg-flex ml-lg-auto">
                      <NavItem className="align-items-end --show-above-small flex-column">
                        <NavLink
                          className={`btn-link nav-link nav-tel p-0${navLocked ? ' nav-link--disabled' : ''}`}
                          to="/consumer/faqs"
                          onClick={navLocked ? handleLockedNavClick : undefined}
                          tabIndex={navLocked ? -1 : undefined}
                          aria-disabled={navLocked || undefined}
                          style={{ lineHeight: 1, fontSize: 16, marginTop: 10, marginLeft: 20, marginRight: 20 }}
                        >
                          FAQs
                        </NavLink>
                      </NavItem>
                      <NavItem className="align-items-end --show-above-small flex-column">
                        <ButtonLink className="btn-link" onClick={navLocked ? undefined : handleModal} isDisabled={navLocked} style={{ lineHeight: 1, fontSize: 14, marginTop: -2, cursor: navLocked ? 'not-allowed' : 'pointer' }}>
                          <strong>Contact Us</strong>
                        </ButtonLink>
                        <a href={`tel:1${numberUnmask(phoneNo)}`} className="p-0 text-right nav-link nav-tel" style={{ lineHeight: 1, marginTop: 3 }}>
                          <strong>{formatPhone(phoneNo)}</strong>
                        </a>
                      </NavItem>
                      <UncontrolledDropdown nav inNavbar>
                        <DropdownToggle nav caret>
                          Account
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem
                            tag="span"
                            disabled={navLocked}
                            onClick={navLocked ? undefined : handlePageNavigation.bind(null, '/consumer/settings')}
                          >
                            Account Settings
                          </DropdownItem>
                          <DropdownItem tag="span" to="/" onClick={handleSignOut}>Sign Out</DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  </Nav>
                </Collapse>
              </>
            )
            : (
              <div style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
              >
                <a href={`tel:+1${numberUnmask(servicingPhone)}`} className="phoneNumber">
                  <span>Questions?&nbsp;</span>
                  <strong>{formatPhone(servicingPhone)}</strong>
                </a>
              </div>
            )
        }
      </Navbar>

      <ContactModal isOpen={modal} toggle={handleModal} />
    </div>
    // <!-- End Portal Header -->
    // End Global Header
  );
};

HeaderComponent.propTypes = {
  signOut: PropTypes.func.isRequired,
  loanDetail: PropTypes.object.isRequired,
};

export default compose(
  connect(
    (state) => ({
      loanDetail: state.dashboard.loanDetail,
    }),
    {
      signOut,
    }
  )
)(HeaderComponent);
